import React, { useCallback, useEffect, useRef, useState } from "react";

import { useHistory, useLocation, useParams } from "react-router";

import { Form, Formik, Field } from "formik";
import * as Yup from "yup";
import Swal from "sweetalert2";

import { registerLocale, setDefaultLocale } from "react-datepicker";
import { differenceInYears, isAfter, parse, isValid } from "date-fns";
import "react-datepicker/dist/react-datepicker.css";
import pt from "date-fns/locale/pt-BR";
import { IoIosWarning } from "react-icons/io";

import {
  Flex,
  Button,
  Grid,
  GridItem,
  Select,
  FormControl,
  FormLabel,
  FormErrorMessage,
  Divider,
  HStack,
  Icon,
  Tooltip,
  Text,
  Box,
} from "@chakra-ui/react";

import CustomDropzone from "../../../../components/CustomDropzone";
import LoadingSpinner from "../../../../components/LoadingSpinner";
import { CustomDatepicker } from "../../../../components/CustomDatepicker";
import { CustomDatepickerWField } from "../../../../components/CustomDatepickerWField";

import PlanejamentoSAService from "../../../../../main/services/PlanejamentoSAService";
import { hasFormChanged } from "../../../../utils/hasFormChanged";
import SemRegistros from "../../../../components/SemRegistros";
import { criancaFunc, servicos } from "../../../../../helpers/Funcionalidades";
import { useAppContext } from "../../../../../Context/MessageContextProvider";
import { useAuthProvider } from "../../../../../Context/AuthProvider";
import { scrollToFieldAnexo } from "../../../../utils/scrollToFIeldAnexo";

const CadastrarPlanejamento = () => {
  const { id } = useParams();
  const history = useHistory();
  const { roleKeycloack, user } = useAuthProvider();
  const { setCadastrarPlanejamentoServ, idPJ } = useAppContext();
  const suportedFormats = [
    ".png",
    ".bmp",
    ".tiff",
    ".jpg",
    ".jpeg",
    ".doc",
    ".docx",
    ".txt",
    ".dot",
    ".pdf",
    ".xml",
    ".xls",
    ".xlam",
    ".ods",
  ];

  const [loading, setLoading] = useState(true);
  const [isLabelOpen, setIsLabelOpen] = useState(false);
  const [initialValues] = useState({
    possuiPlanoTrabalho: "",
    anexoPlanoTrabalho: null,
    dataAtualizacaoPlanoTrabalho: "",
    possuiPPP: "",
    anexoPPP: null,
    dataAtualizacaoPPP: "",
    possuiRelatorioAnual: "",
    anexoRelatorioAnual: null,
    dataApresentacaoRelatorio: "",
    possuiRegimentoInterno: "",
    anexoRegimento: null,
    dataAtualizacaoRegimento: "",
  });

  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const idTipoPendencia = searchParams.get("idTipoPendencia");

  const anexoRef = useRef(null);

  const scrollToField = useCallback(
    (ref) => {
      scrollToFieldAnexo(idTipoPendencia, ref);
    },
    [idTipoPendencia],
  );

  useEffect(() => {
    if (["69"].includes(String(idTipoPendencia))) {
      const timeout = setTimeout(() => {
        requestAnimationFrame(() => {
          if (anexoRef.current) {
            scrollToField(anexoRef);
          }
        });
      }, 300);

      return () => clearTimeout(timeout);
    }
  }, [idTipoPendencia, scrollToField]);

  useEffect(() => {
    setDefaultLocale("pt-br");
    registerLocale("pt-br", pt);

    PlanejamentoSAService.getPlanejamentoSA(id)
      .then((res) => {
        Swal.fire({
          title: "Atenção!",
          text: "Já existe um Planejamento cadastrado para este Serviço de Acolhimento!",
          icon: "warning",
          confirmButtonText: "Fechar",
          confirmButtonColor: "#169499",
        }).then(() => {
          history.push(`/servico-acolhimento/visualizar-planejamento/${id}`);
        });
      })
      .catch((erro) => {
        if (erro.response) {
          if (erro.response.data.status !== 404) {
            Swal.fire({
              title: "Atenção!",
              text: "Já existe um Planejamento cadastrado para este Serviço de Acolhimento!",
              icon: "warning",
              confirmButtonText: "Fechar",
              confirmButtonColor: "#169499",
            }).then(() => {
              history.push(
                `/servico-acolhimento/visualizar-planejamento/${id}`,
              );
            });
          } else {
            setLoading(false);
          }
        }
      });
  }, [history, id]);

  const schema = Yup.object().shape({
    possuiPlanoTrabalho: Yup.string("Este campo é obrigatório!").required(
      "Este campo é obrigatório!",
    ),
    anexoPlanoTrabalho: Yup.mixed().when("possuiPlanoTrabalho", {
      is: (possui) => (possui ? possui === "S" : false),
      then: Yup.mixed()
        .required("Este campo é obrigatório!")
        .test("fileSize", "O arquivo tem mais de 10MB.", (value) => {
          if (value === null) {
            return true;
          } else {
            return value.size <= 10000000;
          }
        })
        .test("fileType", "Anexar uma extensão válida.", (value) => {
          if (value === null) {
            return true;
          } else {
            let haveFormat = false;
            suportedFormats.forEach((format) => {
              if (value.path.toString().toLowerCase().includes(format)) {
                haveFormat = true;
              }
            });
            return haveFormat;
          }
        }),
    }),
    dataAtualizacaoPlanoTrabalho: Yup.string()
      .nullable()
      .when("possuiPlanoTrabalho", {
        is: (possui) => (possui ? possui === "S" : false),
        then: Yup.string("Este campo é obrigatório!")
          .nullable()
          .required("Este campo é obrigatório!")
          .test(
            "dataAtualizacaoAfterToday",
            "A data informada não pode ser uma data futura!",
            (value) => {
              if (!Boolean(value)) {
                return false;
              } else {
                return !isAfter(
                  parse(value, "dd/MM/yyyy", new Date()),
                  new Date(),
                );
              }
            },
          )
          .test("validDate", "Data inválida!", (value) => {
            if (!!value) {
              if (value.length === 10) {
                return isValid(parse(value, "dd/MM/yyyy", new Date()));
              } else {
                return false;
              }
            } else {
              return false;
            }
          }),
      }),
    possuiPPP: Yup.string("Este campo é obrigatório!").required(
      "Este campo é obrigatório!",
    ),
    anexoPPP: Yup.mixed().when("possuiPPP", {
      is: (possui) => (possui ? possui === "S" : false),
      then: Yup.mixed()
        .required("Este campo é obrigatório!")
        .test("fileSize", "O arquivo tem mais de 10MB.", (value) => {
          if (value === null) {
            return true;
          } else {
            return value.size <= 10000000;
          }
        })
        .test("fileType", "Anexar uma extensão válida.", (value) => {
          if (value === null) {
            return true;
          } else {
            let haveFormat = false;
            suportedFormats.forEach((format) => {
              if (value.path.toString().toLowerCase().includes(format)) {
                haveFormat = true;
              }
            });
            return haveFormat;
          }
        }),
    }),
    dataAtualizacaoPPP: Yup.string()
      .nullable()
      .when("possuiPPP", {
        is: (possui) => (possui ? possui === "S" : false),
        then: Yup.string("Este campo é obrigatório!")
          .nullable()
          .required("Este campo é obrigatório!")
          .test(
            "dataAtualizacaoAfterToday",
            "A data informada não pode ser uma data futura!",
            (value) => {
              if (!Boolean(value)) {
                return false;
              } else {
                return !isAfter(
                  parse(value, "dd/MM/yyyy", new Date()),
                  new Date(),
                );
              }
            },
          )
          .test("validDate", "Data inválida!", (value) => {
            if (!!value) {
              if (value.length === 10) {
                return isValid(parse(value, "dd/MM/yyyy", new Date()));
              } else {
                return false;
              }
            } else {
              return false;
            }
          }),
      }),
    possuiRelatorioAnual: Yup.string("Este campo é obrigatório!").required(
      "Este campo é obrigatório!",
    ),
    anexoRelatorioAnual: Yup.mixed().when("possuiRelatorioAnual", {
      is: (possui) => (possui ? possui === "S" : false),
      then: Yup.mixed()
        .required("Este campo é obrigatório!")
        .test("fileSize", "O arquivo tem mais de 10MB.", (value) => {
          if (value === null) {
            return true;
          } else {
            return value.size <= 10000000;
          }
        })
        .test("fileType", "Anexar uma extensão válida.", (value) => {
          if (value === null) {
            return true;
          } else {
            let haveFormat = false;
            suportedFormats.forEach((format) => {
              if (value.path.toString().toLowerCase().includes(format)) {
                haveFormat = true;
              }
            });
            return haveFormat;
          }
        }),
    }),
    dataApresentacaoRelatorio: Yup.string()
      .nullable()
      .when("possuiRelatorioAnual", {
        is: (possui) => (possui ? possui === "S" : false),
        then: Yup.string("Este campo é obrigatório!")
          .nullable()
          .required("Este campo é obrigatório!")
          .test(
            "dataAtualizacaoAfterToday",
            "A data informada não pode ser uma data futura!",
            (value) => {
              if (!Boolean(value)) {
                return false;
              } else {
                return !isAfter(
                  parse(value, "dd/MM/yyyy", new Date()),
                  new Date(),
                );
              }
            },
          )
          .test("validDate", "Data inválida!", (value) => {
            if (!!value) {
              if (value.length === 10) {
                return isValid(parse(value, "dd/MM/yyyy", new Date()));
              } else {
                return false;
              }
            } else {
              return false;
            }
          }),
      }),
    possuiRegimentoInterno: Yup.string("Este campo é obrigatório!").required(
      "Este campo é obrigatório!",
    ),
    anexoRegimento: Yup.mixed().when("possuiRegimentoInterno", {
      is: (possui) => (possui ? possui === "S" : false),
      then: Yup.mixed()
        .required("Este campo é obrigatório!")
        .test("fileSize", "O arquivo tem mais de 10MB.", (value) => {
          if (value === null) {
            return true;
          } else {
            return value.size <= 10000000;
          }
        })
        .test("fileType", "Anexar uma extensão válida.", (value) => {
          if (value === null) {
            return true;
          } else {
            let haveFormat = false;
            suportedFormats.forEach((format) => {
              if (value.path.toString().toLowerCase().includes(format)) {
                haveFormat = true;
              }
            });
            return haveFormat;
          }
        }),
    }),
    dataAtualizacaoRegimento: Yup.string()
      .nullable()
      .when("possuiRegimentoInterno", {
        is: (possui) => (possui ? possui === "S" : false),
        then: Yup.string("Este campo é obrigatório!")
          .nullable()
          .required("Este campo é obrigatório!")
          .test(
            "dataAtualizacaoAfterToday",
            "A data informada não pode ser uma data futura!",
            (value) => {
              if (!Boolean(value)) {
                return false;
              } else {
                return !isAfter(
                  parse(value, "dd/MM/yyyy", new Date()),
                  new Date(),
                );
              }
            },
          )
          .test("validDate", "Data inválida!", (value) => {
            if (!!value) {
              if (value.length === 10) {
                return isValid(parse(value, "dd/MM/yyyy", new Date()));
              } else {
                return false;
              }
            } else {
              return false;
            }
          }),
      }),
  });

  const handleSubmit = (values, actions) => {
    setTimeout(() => {
      const {
        dataAtualizacaoPlanoTrabalho,
        dataAtualizacaoPPP,
        dataApresentacaoRelatorio,
        dataAtualizacaoRegimento,
        ...resto
      } = values;

      const valuesToSend = {
        dataAtualizacaoPlanoTrabalho: dataAtualizacaoPlanoTrabalho
          ? parse(
              dataAtualizacaoPlanoTrabalho,
              "dd/MM/yyyy",
              new Date(),
            ).toISOString()
          : "",
        dataAtualizacaoPPP: dataAtualizacaoPPP
          ? parse(dataAtualizacaoPPP, "dd/MM/yyyy", new Date()).toISOString()
          : "",
        dataApresentacaoRelatorio: dataApresentacaoRelatorio
          ? parse(
              dataApresentacaoRelatorio,
              "dd/MM/yyyy",
              new Date(),
            ).toISOString()
          : "",
        dataAtualizacaoRegimento: dataAtualizacaoRegimento
          ? parse(
              dataAtualizacaoRegimento,
              "dd/MM/yyyy",
              new Date(),
            ).toISOString()
          : "",
        ...resto,
      };

      PlanejamentoSAService.novoPlanejamentoSA(id, valuesToSend)
        .then((res) => {
          Swal.fire({
            title: "Sucesso!",
            text: "Registro cadastrado com sucesso!",
            icon: "success",
            confirmButtonText: "Continuar",
            confirmButtonColor: "#169499",
          }).then(function () {
            history.push("/servico-acolhimento/visualizar-planejamento/" + id);
            setCadastrarPlanejamentoServ(false);
          });
          actions.setSubmitting(false);
        })
        .catch((error) => {
          if (error.response) {
            if (error.response.data) {
              if (error.response.data.message) {
                if (error.response.data.field === "planejamento") {
                  Swal.fire({
                    title: "Atenção!",
                    text: error.response.data.message
                      ? JSON.parse(error.response.data.message).planejamento
                      : "",
                    icon: "warning",
                    confirmButtonText: "Fechar",
                    confirmButtonColor: "#169499",
                  }).then(() => {});
                } else {
                  const errors = JSON.parse(error.response.data.message);
                  for (let key in errors) {
                    actions.setFieldError(key.toString(), errors[key]);
                  }
                }
              } else if (error.response.data.apierror) {
                if (error.response.data.apierror.subErrors) {
                  error.response.data.apierror.subErrors.forEach((erro) => {
                    actions.setFieldError(erro.field, erro.message);
                  });
                }
              }
            }
          } else {
            console.log(error);
          }
          actions.setSubmitting(false);
        });
    }, 200);
  };

  return roleKeycloack &&
    (roleKeycloack.includes(servicos.planejamentoCadastrar) ||
      (roleKeycloack?.includes(criancaFunc.visualizarTudoAcolhimento) &&
        user?.orgaoExterno?.includes(idPJ))) ? (
    !loading ? (
      <Formik
        validationSchema={schema}
        initialValues={initialValues}
        onSubmit={handleSubmit}
        validateOnChange={false}
        validateOnBlur={false}
      >
        {(props) => (
          <Form>
            {setCadastrarPlanejamentoServ(
              hasFormChanged(initialValues, props?.values),
            )}
            <Box
              mt={"15px"}
              border={
                ["69"].includes(idTipoPendencia) ? "3px solid #4299E199" : ""
              }
              ref={anexoRef}
            >
              <Flex
                bgColor="#FFF"
                p="25px"
                borderRadius="16px"
                flexDir="column"
              >
                <Text color={"#169499"} fontSize="lg" fontWeight="bold">
                  PLANO DE TRABALHO
                </Text>
                <Divider orientation="horizontal" my="15px" />
                <Grid
                  templateColumns={{
                    base: "repeat(1, 1fr)",
                    lg: "repeat(3, 1fr)",
                  }}
                  gap="40px"
                >
                  <GridItem colSpan={1}>
                    <Field name="possuiPlanoTrabalho">
                      {({ field, form }) => (
                        <FormControl
                          isInvalid={
                            form.errors.possuiPlanoTrabalho &&
                            form.touched.possuiPlanoTrabalho
                          }
                          id="possuiPlanoTrabalho"
                        >
                          <FormLabel
                            color="#5F6377"
                            fontWeight="500"
                            fontSize="14px"
                            lineHeight="19.6px"
                            htmlFor="possuiPlanoTrabalho"
                          >
                            Possui Plano de Trabalho?*
                          </FormLabel>
                          <Select
                            {...field}
                            h="42px"
                            name="possuiPlanoTrabalho"
                            id="possuiPlanoTrabalho"
                            placeholder="Selecione"
                            backgroundColor="white"
                          >
                            <option value="S">Sim</option>
                            <option value="N">Não</option>
                          </Select>
                          <FormErrorMessage>
                            {form.errors.possuiPlanoTrabalho}
                          </FormErrorMessage>
                        </FormControl>
                      )}
                    </Field>
                  </GridItem>
                  {props.values.possuiPlanoTrabalho === "S" && (
                    <>
                      <Grid
                        templateColumns={{
                          base: "repeat(1, 1fr) 0.3fr",
                          lg: "repeat(1, 1fr) 0.3fr",
                        }}
                      >
                        <GridItem colSpan={1}>
                          <CustomDropzone
                            formProps={props}
                            title="Anexar Plano de Trabalho*"
                            fieldName="anexoPlanoTrabalho"
                          />
                        </GridItem>
                      </Grid>
                      <GridItem colSpan={1}>
                        <Field name="dataAtualizacaoPlanoTrabalho">
                          {({ field, form }) => (
                            <FormControl
                              isInvalid={
                                form.errors.dataAtualizacaoPlanoTrabalho &&
                                form.touched.dataAtualizacaoPlanoTrabalho
                              }
                              id="dataAtualizacaoPlanoTrabalho"
                            >
                              <FormLabel
                                color="#5F6377"
                                fontWeight="500"
                                fontSize="14px"
                                lineHeight="19.6px"
                                htmlFor="dataAtualizacaoPlanoTrabalho"
                              >
                                Última Atualização*
                              </FormLabel>
                              <HStack>
                                <CustomDatepicker
                                  value={
                                    props.values.dataAtualizacaoPlanoTrabalho
                                  }
                                  setValue={(date) =>
                                    form.setFieldValue(
                                      "dataAtualizacaoPlanoTrabalho",
                                      date,
                                    )
                                  }
                                  placeholder="DD/MM/AAAA"
                                />
                                {(props.values.dataAtualizacaoPlanoTrabalho
                                  ? isValid(
                                      parse(
                                        props.values
                                          .dataAtualizacaoPlanoTrabalho,
                                        "dd/MM/yyyy",
                                        new Date(),
                                      ),
                                    )
                                    ? differenceInYears(
                                        new Date(),
                                        parse(
                                          props.values
                                            .dataAtualizacaoPlanoTrabalho,
                                          "dd/MM/yyyy",
                                          new Date(),
                                        ),
                                      ) >= 2
                                    : false
                                  : false) && (
                                  <Tooltip
                                    label={
                                      "O Plano de Trabalho deve ser atualizado, no máximo, a cada 02 anos" +
                                      " - período correspondente à reavaliação do programa pelo CMDCA (ECA - Art. 90, § 3°)."
                                    }
                                    fontSize="md"
                                    isOpen={isLabelOpen}
                                  >
                                    <span>
                                      <Icon
                                        aria-label="Aviso"
                                        color="#950000"
                                        as={IoIosWarning}
                                        boxSize="24px"
                                        onMouseEnter={() =>
                                          setIsLabelOpen(true)
                                        }
                                        onMouseLeave={() =>
                                          setIsLabelOpen(false)
                                        }
                                        onClick={() => setIsLabelOpen(true)}
                                      />
                                    </span>
                                  </Tooltip>
                                )}
                              </HStack>
                              <FormErrorMessage>
                                {form.errors.dataAtualizacaoPlanoTrabalho}
                              </FormErrorMessage>
                            </FormControl>
                          )}
                        </Field>
                      </GridItem>
                    </>
                  )}
                </Grid>
              </Flex>

              <Flex
                bgColor="#FFF"
                p="25px"
                mt="15px"
                borderRadius="16px"
                flexDir="column"
              >
                <Text color={"#169499"} fontSize="lg" fontWeight="bold">
                  PROJETO POLÍTICO-PEDAGÓGICO
                </Text>
                <Divider orientation="horizontal" my="15px" />
                <Grid
                  templateColumns={{
                    base: "repeat(1, 1fr)",
                    lg: "repeat(3, 1fr)",
                  }}
                  gap="40px"
                  mt="10px"
                >
                  <GridItem colSpan={1}>
                    <Field name="possuiPPP">
                      {({ field, form }) => (
                        <FormControl
                          isInvalid={
                            form.errors.possuiPPP && form.touched.possuiPPP
                          }
                          id="possuiPPP"
                        >
                          <FormLabel
                            color="#5F6377"
                            fontWeight="500"
                            fontSize="14px"
                            lineHeight="19.6px"
                            htmlFor="possuiPPP"
                          >
                            Possui Projeto Político-Pedagógico?*
                          </FormLabel>
                          <Select
                            {...field}
                            h="42px"
                            name="possuiPPP"
                            id="possuiPPP"
                            placeholder="Selecione"
                            backgroundColor="white"
                          >
                            <option value="S">Sim</option>
                            <option value="N">Não</option>
                          </Select>
                          <FormErrorMessage>
                            {form.errors.possuiPPP}
                          </FormErrorMessage>
                        </FormControl>
                      )}
                    </Field>
                  </GridItem>

                  {props.values.possuiPPP === "S" && (
                    <>
                      <Grid
                        templateColumns={{
                          base: "repeat(1, 1fr) 0.3fr",
                          lg: "repeat(1, 1fr) 0.3fr",
                        }}
                      >
                        <GridItem colSpan={1}>
                          <Flex>
                            <CustomDropzone
                              formProps={props}
                              title="Anexar Projeto Político-Pedagógico*"
                              fieldName="anexoPPP"
                            />
                          </Flex>
                        </GridItem>
                      </Grid>
                      <GridItem colSpan={1}>
                        <CustomDatepickerWField
                          fieldName="dataAtualizacaoPPP"
                          label="Última Atualização*"
                          placeholder="DD/MM/AAAA"
                        />
                      </GridItem>
                    </>
                  )}
                </Grid>
              </Flex>

              <Flex
                bgColor="#FFF"
                p="25px"
                mt="15px"
                borderRadius="16px"
                flexDir="column"
              >
                <Text color={"#169499"} fontSize="lg" fontWeight="bold">
                  RELATÓRIO ANUAL
                </Text>
                <Divider orientation="horizontal" my="15px" />
                <Grid
                  templateColumns={{
                    base: "repeat(1, 1fr)",
                    lg: "repeat(3, 1fr)",
                  }}
                  gap="40px"
                  mt="10px"
                >
                  <GridItem colSpan={1}>
                    <Field name="possuiRelatorioAnual">
                      {({ field, form }) => (
                        <FormControl
                          isInvalid={
                            form.errors.possuiRelatorioAnual &&
                            form.touched.possuiRelatorioAnual
                          }
                          id="possuiRelatorioAnual"
                        >
                          <FormLabel
                            color="#5F6377"
                            fontWeight="500"
                            fontSize="14px"
                            lineHeight="19.6px"
                            htmlFor="possuiRelatorioAnual"
                          >
                            Possui Relatório Anual?*
                          </FormLabel>
                          <Select
                            {...field}
                            h="42px"
                            name="possuiRelatorioAnual"
                            id="possuiRelatorioAnual"
                            placeholder="Selecione"
                            backgroundColor="white"
                          >
                            <option value="S">Sim</option>
                            <option value="N">Não</option>
                          </Select>
                          <FormErrorMessage>
                            {form.errors.possuiRelatorioAnual}
                          </FormErrorMessage>
                        </FormControl>
                      )}
                    </Field>
                  </GridItem>
                  {props.values.possuiRelatorioAnual === "S" && (
                    <>
                      <Grid
                        templateColumns={{
                          base: "repeat(1, 1fr) 0.3fr",
                          lg: "repeat(1, 1fr) 0.3fr",
                        }}
                      >
                        <GridItem colSpan={1}>
                          <Flex>
                            <CustomDropzone
                              formProps={props}
                              title="Anexar Relatório Anual*"
                              fieldName="anexoRelatorioAnual"
                            />
                          </Flex>
                        </GridItem>
                      </Grid>
                      <GridItem colSpan={1}>
                        <CustomDatepickerWField
                          fieldName="dataApresentacaoRelatorio"
                          label="Última Atualização*"
                          placeholder="DD/MM/AAAA"
                        />
                      </GridItem>
                    </>
                  )}
                </Grid>
              </Flex>

              <Flex
                bgColor="#FFF"
                p="25px"
                mt="15px"
                borderRadius="16px"
                flexDir="column"
              >
                <Text color={"#169499"} fontSize="lg" fontWeight="bold">
                  REGIMENTO INTERNO
                </Text>
                <Divider orientation="horizontal" my="15px" />
                <Grid
                  templateColumns={{
                    base: "repeat(1, 1fr)",
                    lg: "repeat(3, 1fr)",
                  }}
                  gap="40px"
                  mt="10px"
                >
                  <GridItem colSpan={1}>
                    <Field name="possuiRegimentoInterno">
                      {({ field, form }) => (
                        <FormControl
                          isInvalid={
                            form.errors.possuiRegimentoInterno &&
                            form.touched.possuiRegimentoInterno
                          }
                          id="possuiRegimentoInterno"
                        >
                          <FormLabel
                            color="#5F6377"
                            fontWeight="500"
                            fontSize="14px"
                            lineHeight="19.6px"
                            htmlFor="possuiRegimentoInterno"
                          >
                            Possui Regimento Interno?*
                          </FormLabel>
                          <Select
                            {...field}
                            h="42px"
                            name="possuiRegimentoInterno"
                            id="possuiRegimentoInterno"
                            placeholder="Selecione"
                            backgroundColor="white"
                          >
                            <option value="S">Sim</option>
                            <option value="N">Não</option>
                          </Select>
                          <FormErrorMessage>
                            {form.errors.possuiRegimentoInterno}
                          </FormErrorMessage>
                        </FormControl>
                      )}
                    </Field>
                  </GridItem>
                  {props.values.possuiRegimentoInterno === "S" && (
                    <>
                      <Grid
                        templateColumns={{
                          base: "repeat(1, 1fr) 0.3fr",
                          lg: "repeat(1, 1fr) 0.3fr",
                        }}
                      >
                        <GridItem colSpan={1}>
                          <Flex>
                            <CustomDropzone
                              formProps={props}
                              title="Anexar Regimento Interno*"
                              fieldName="anexoRegimento"
                            />
                          </Flex>
                        </GridItem>
                      </Grid>
                      <GridItem colSpan={1}>
                        <CustomDatepickerWField
                          fieldName="dataAtualizacaoRegimento"
                          label="Última Atualização*"
                          placeholder="DD/MM/AAAA"
                        />
                      </GridItem>
                    </>
                  )}
                </Grid>
              </Flex>
            </Box>
            <Flex mt="30px" justifyContent="flex-end">
              <Button
                id="button-salvar"
                type="submit"
                translate="no"
                color="#FFF"
                backgroundColor="#169499"
                fontWeight={700}
                fontSize="15px"
                lineHeight="21.6px"
                _hover={{ bg: "#169499" }}
                w="17%"
                isLoading={props.isSubmitting}
              >
                Salvar
              </Button>
            </Flex>
          </Form>
        )}
      </Formik>
    ) : (
      <Flex w="100%" mt="25%" justifyContent="center">
        <LoadingSpinner />
      </Flex>
    )
  ) : (
    <Box mt={2}>
      <SemRegistros buttonVisible={true} half />
    </Box>
  );
};

export default CadastrarPlanejamento;
